import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AgreementEligibilityCode } from "@edp-enums/agreement-eligibility.enum";
import { TypeEventOperationHistory } from "@edp-enums/type-event-operation.enum";
import { PaymentAgreement, SelectedComponentEnum } from "@edp-models/edp-payment-agreement.model";
import { AgreementEligibilityError, AgreementEligibilityResponse } from "@edp-models/edp-payment.model";
import { EdpPaymentAgreementService } from "@edp-services/edp-payment-agreement.service";
import { EdpRecaptchaService } from "@edp-services/edpRecaptchaService.service";
import { NifService } from "@edp-services/nif.service";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { PaymentService } from "@edp-services/payment.service";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { IAppState } from "@edp-store/state/app.state";
import { Store } from "@ngrx/store";

@Component({
    selector: "eligibility-analysis",
    templateUrl: "./eligibility-analysis.component.html",
    styleUrls: ["./eligibility-analysis.component.scss"],
})
export class EligibiliAnalysisComponent implements OnInit {
    private nif: string;
    public agreementEligibility: AgreementEligibilityResponse;
    public recaptchaToken: string;
    public selectedIndex: number | null = null;
    public isPage: boolean;
    public accountNumber: string;
    public recaptcha: any;

    constructor(
        private paymentService: PaymentService,
        private nifService: NifService,
        private recaptchaService: EdpRecaptchaService,
        private store: Store<IAppState>,
        private router: Router,
        private edpPaymentAgreementService: EdpPaymentAgreementService
    ) {
        this.nif = this.nifService.getNifValue().value;
        this.isPage = this.edpPaymentAgreementService.getBottomBarValue();
    }

    ngOnInit(): void {
        this.recaptchaToken = this.recaptchaService.getContactResponse();
        this.callEligibilityAgreement();
    }

    public callEligibilityAgreement() {
        this.store.dispatch(new ShowLoader(true));
        const args = {
            nif: this.nif,
            recaptcha: this.recaptchaToken,
        };
        try {
            this.paymentService.eligibilityAgreement(args).subscribe(
                (res: any) => {
                    const response = res[0] as AgreementEligibilityResponse;
                    const accountId = response.data?.accountNumber;

                    if (response?.debits && response?.debits?.length === 1) {
                        this.selectCard(0, response.debits[0].accountNumber);
                    }

                    if (response.status && response.code === AgreementEligibilityCode.STATUS_APROVED) {
                        this.callAgreementCheckout(accountId);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (response.status && response.code === AgreementEligibilityCode.STATUS_ELIGIBLE) {
                        this.agreementEligibility = response;
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (response.status && response.code === AgreementEligibilityCode.STATUS_OPEN) {
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.WAITING_VERIFICATION);
                        this.edpPaymentAgreementService.setAccount(accountId);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (!response.status && response.code === AgreementEligibilityCode.STATUS_WAITING_ACCEPTANCE) {
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.CURRENT_PAYMENT_AGREEMENT);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (!response.status && response.code !== AgreementEligibilityCode.STATUS_WAITING_ACCEPTANCE) {
                        this.handleNoAgreementRedirection();
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (response.error === AgreementEligibilityError.EF006) {
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (
                        response.data.error === AgreementEligibilityError.E001 ||
                        response.data.error === AgreementEligibilityError.E002 ||
                        response.data.error === AgreementEligibilityError.E003 ||
                        response.data.error === AgreementEligibilityError.E006 ||
                        response.data.error === AgreementEligibilityError.E007 ||
                        response.data.error === AgreementEligibilityError.E008 ||
                        response.data.error === AgreementEligibilityError.E009 ||
                        response.data.error === AgreementEligibilityError.E010 ||
                        response.data.error === AgreementEligibilityError.E011
                    ) {
                        this.edpPaymentAgreementService.setIneligibleClient(true);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    if (response.data.error === AgreementEligibilityError.E004 || response.data.error === AgreementEligibilityError.E005) {
                        this.edpPaymentAgreementService.setIneligibleClient(false);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                        this.store.dispatch(new ShowLoader(false));
                        return;
                    }

                    this.agreementEligibility = response;

                    this.store.dispatch(new ShowLoader(false));
                },
                err => {
                    this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.GENERIC_ERROR);
                    this.store.dispatch(new ShowLoader(false));
                    console.error(err);
                }
            );
        } catch (error) {
            this.store.dispatch(new ShowLoader(false));
            console.error(error);
        }
    }

    public isEven(index: number): boolean {
        return index % 2 === 0;
    }

    public selectCard(index: number, accountNumber: string) {
        this.edpPaymentAgreementService.setAccount(accountNumber);
        this.selectedIndex = index;
        this.accountNumber = accountNumber;
        this.setOperationHistory(this.nif, TypeEventOperationHistory.SELECTCONTRACT, accountNumber);
    }

    public agreementGenerate() {
        this.store.dispatch(new ShowLoader(true));
        const args = {
            nif: this.nif,
            recaptcha: this.recaptchaToken,
            account: this.accountNumber,
        };
        this.paymentService.agreementGenerate(args).subscribe(
            (res: any) => {
                this.setOperationHistory(this.nif, TypeEventOperationHistory.RESPONSEREQUESTAGREEMENT, this.accountNumber);
                this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.WAITING_VERIFICATION);
                this.store.dispatch(new ShowLoader(false));
            },
            error => {
                console.error(error);
                this.store.dispatch(new ShowLoader(false));
            }
        );
    }

    onAgreementGenerate(captchaRef: any): void {
        captchaRef.execute();
        this.recaptcha = captchaRef;
        this.setOperationHistory(this.nif, TypeEventOperationHistory.REQUESTAGREEMENT, this.accountNumber);
    }

    public resolved(captchaResponse: string): void {
        this.recaptchaToken = captchaResponse;
        if (this.recaptchaToken) {
            this.agreementGenerate();
        }
    }

    public handleNoAgreementRedirection() {
        switch (true) {
            case !this.isPage:
                this.router.navigate(["area-aberta/acordo-pagamento/sem-acordo"]);
                return;
            case this.isPage:
                this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                return;
        }
    }

    private setOperationHistory(nif: string, operation: string, account?: string) {
        this.paymentService.agreementHistory({ nif, operation, account }).subscribe(() => {});
    }

    public callAgreementCheckout(accountId: string) {
        const args = {
            nif: this.nif,
            account: accountId,
        };
        try {
            this.paymentService.agreementCheckout(args).subscribe(
                (response: any) => {
                    const res = response[0] as PaymentAgreement;
                    this.edpPaymentAgreementService.setPaymentAgreementTerm(res);
                    this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.PAYMENT_AGREEMENT_TERM);
                    this.store.dispatch(new ShowLoader(false));
                },
                error => console.error(error)
            );
        } catch (error) {
            console.error(error);
        }
    }
}
